import React from "react";
import { motion } from "framer-motion";
import { MapPin, Navigation } from "lucide-react";

/* Floating UI cards used on Home / Services / Technology hero, mimicking the
   screenshots: live tracking map mini-card, safety status, parent alert,
   attendance, today's operations, etc. */

export const LiveTrackingCard = ({ delay = 0.2 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
    className="bg-white rounded-2xl shadow-[0_18px_45px_-25px_rgba(15,27,45,0.35)] border border-slate-100 p-4 w-[260px]"
    data-testid="live-tracking-card"
  >
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2 text-[#0F1B2D] text-sm font-semibold">
        <MapPin size={16} className="text-[#2563EB]" />
        Live Tracking
      </div>
      <div className="flex items-center gap-1.5 text-[12px] text-emerald-600 font-semibold">
        <span className="w-2 h-2 rounded-full bg-emerald-500 live-dot" />
        Live
      </div>
    </div>
    <div className="flex items-center justify-between text-[12px] text-slate-500 mb-2">
      <span className="font-semibold text-[#0F1B2D]">Bus KA 01 AB 1234</span>
      <span>Route 7A</span>
    </div>
    <div className="relative h-[88px] rounded-lg bg-slate-50 overflow-hidden">
      <svg viewBox="0 0 240 90" className="w-full h-full">
        <defs>
          <pattern
            id="grid"
            width="20"
            height="20"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 20 0 L 0 0 0 20"
              fill="none"
              stroke="#e2e8f0"
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        <rect width="240" height="90" fill="url(#grid)" />
        <polyline
          points="10,60 50,40 95,55 140,30 190,45 230,20"
          fill="none"
          stroke="#2563EB"
          strokeWidth="2"
        />
        {[10, 50, 95, 140, 190, 230].map((x, i) => (
          <circle
            key={i}
            cx={x}
            cy={[60, 40, 55, 30, 45, 20][i]}
            r="3"
            fill="#2563EB"
          />
        ))}
        <g transform="translate(120,30)">
          <rect x="-12" y="-7" width="24" height="14" rx="3" fill="#F5A524" />
          <rect x="-9" y="-5" width="6" height="4" fill="#fff" />
          <rect x="3" y="-5" width="6" height="4" fill="#fff" />
        </g>
      </svg>
    </div>
    <div className="flex items-center justify-between mt-3 text-sm">
      <span className="text-slate-500">ETA to School</span>
      <span className="font-bold text-[#2563EB]">08:24 AM</span>
    </div>
  </motion.div>
);

export const SafetyStatusCard = ({ delay = 0.35 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
    className="bg-white rounded-2xl shadow-[0_18px_45px_-25px_rgba(15,27,45,0.35)] border border-slate-100 p-4 w-[260px]"
    data-testid="safety-status-card"
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 font-semibold text-[#0F1B2D]">
        <div className="w-7 h-7 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2 4 6v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V6l-8-4z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <path
              d="M9 12l2 2 4-4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        Safety Status
      </div>
      <span className="text-emerald-600 font-bold text-sm">All Good</span>
    </div>
    <div className="space-y-2 text-sm">
      <div className="flex items-center justify-between">
        <span className="text-slate-500">Speed</span>
        <span className="font-semibold text-[#0F1B2D]">40 km/h</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-slate-500">Last Stop</span>
        <span className="font-semibold text-[#2563EB]">Pickle Park</span>
      </div>
    </div>
  </motion.div>
);

export const ParentAlertCard = ({ delay = 0.5 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
    className="bg-white rounded-2xl shadow-[0_18px_45px_-25px_rgba(15,27,45,0.35)] border border-slate-100 p-4 w-[260px]"
    data-testid="parent-alert-card"
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 font-semibold text-[#0F1B2D]">
        <div className="w-7 h-7 rounded-full bg-blue-50 text-[#2563EB] flex items-center justify-center">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.7 21a2 2 0 0 1-3.4 0"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
        Parent Alert
      </div>
      <span className="text-xs text-slate-400">7:30 AM</span>
    </div>
    <div className="flex items-center gap-3">
      <div className="text-sm text-slate-700 leading-snug">
        Your child <span className="font-bold text-[#0F1B2D]">Aarav</span> has
        boarded the bus
      </div>
      <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-lg font-bold text-amber-700">
        A
      </div>
    </div>
  </motion.div>
);

export const AttendanceCard = ({ delay = 0.65 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
    className="bg-white rounded-2xl shadow-[0_18px_45px_-25px_rgba(15,27,45,0.35)] border border-slate-100 p-4 w-[260px]"
    data-testid="attendance-card"
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 font-semibold text-[#0F1B2D]">
        <div className="w-7 h-7 rounded-full bg-blue-50 text-[#2563EB] flex items-center justify-center">
          <Navigation size={14} />
        </div>
        Attendance
      </div>
      <span className="text-xs text-slate-400">Today</span>
    </div>
    <div className="grid grid-cols-3 text-center">
      <div>
        <div className="text-[11px] text-slate-500 mb-1">On Time</div>
        <div className="text-emerald-600 font-extrabold text-xl">42</div>
      </div>
      <div>
        <div className="text-[11px] text-slate-500 mb-1">Boarded</div>
        <div className="text-[#2563EB] font-extrabold text-xl">40</div>
      </div>
      <div>
        <div className="text-[11px] text-slate-500 mb-1">Not Boarded</div>
        <div className="text-rose-500 font-extrabold text-xl">2</div>
      </div>
    </div>
  </motion.div>
);

export const TodayOperationsCard = ({ delay = 0.5 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
    className="bg-white rounded-2xl shadow-[0_18px_45px_-25px_rgba(15,27,45,0.35)] border border-slate-100 p-4 w-[260px]"
    data-testid="today-operations-card"
  >
    <div className="flex items-center gap-2 font-semibold text-[#0F1B2D] mb-3">
      <div className="w-7 h-7 rounded-full bg-blue-50 text-[#2563EB] flex items-center justify-center">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <rect
            x="3"
            y="4"
            width="18"
            height="18"
            rx="2"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M16 2v4M8 2v4M3 10h18"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      </div>
      Today's Operations
    </div>
    <div className="space-y-2 text-sm">
      <div className="flex items-center justify-between">
        <span className="text-slate-500">Trips Completed</span>
        <span className="font-bold text-[#0F1B2D]">12</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-slate-500">On-Time Performance</span>
        <span className="font-bold text-emerald-600">98%</span>
      </div>
    </div>
  </motion.div>
);
